import { IntegrationType, NonSensitiveFlightRecordConnectedAccount } from "@nova/schemas"
import { ConnectedAccountIntegrationSection } from "./ConnectedAccountIntegrationSection"
import { usePilotTrackingConnectionsContext } from "features/data-management/pilot-tracking/PilotTrackingConnectionsContext"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { BeforeYouSyncModalDji } from "../modals/sync/BeforeYouSyncModalDji"
import { ConnectedAccountEditDjiModal } from "../modals/accounts/edit/ConnectedAccountEditDjiModal"
import { useCallback } from "react"
import * as Icons from "@nova/component-icons"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { ConnectedAccountDeleteDjiModal } from "../modals/accounts/delete/ConnectedAccountDeleteDjiModal"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"

export const ConnectedAccountIntegrationSectionDji = ({
    accounts,
}: {
    accounts: NonSensitiveFlightRecordConnectedAccount<IntegrationType.dji>[]
}) => {
    const dispatch = useAppDispatch()
    const confirmationModal = useConfirmationModalContext()
    const { triggerDjiSync, updateDjiConnection, deleteDjiConnection } =
        usePilotTrackingConnectionsContext()

    const showSyncModal = async (id: string) => {
        try {
            await confirmationModal.confirm({
                RenderComponent: BeforeYouSyncModalDji,
                asyncConfirmCallback: () => triggerDjiSync(id),
            })
        } catch (err) {
            dispatch(
                setGlobalNotificationContext({
                    message: err instanceof Error ? err.message : "Failed to start sync",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    const showEditModal = useCallback(
        async (account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.dji>) => {
            void confirmationModal.confirm({
                RenderComponent: (props) => (
                    <ConnectedAccountEditDjiModal
                        {...props}
                        account={account}
                        onSave={updateDjiConnection}
                        onDelete={() => deleteDjiConnection(account.id)}
                    />
                ),
            })
        },
        [confirmationModal, updateDjiConnection, deleteDjiConnection]
    )

    const showDeleteModal = useCallback(
        async (account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.dji>) => {
            await confirmationModal.confirm({
                RenderComponent: (props) => (
                    <ConnectedAccountDeleteDjiModal
                        {...props}
                        account={account}
                        onDelete={() => deleteDjiConnection(account.id)}
                    />
                ),
            })
        },
        [confirmationModal, deleteDjiConnection]
    )

    const createContextMenuSections = useCallback(
        (
            account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.dji>
        ): ContextMenuItem[][] => {
            return [
                [
                    {
                        title: "Edit Details",
                        Icon: Icons.Pencil,
                        onClick: () => showEditModal(account),
                    },
                ],
                [
                    {
                        title: "Disconnect Account",
                        Icon: Icons.LinkOff,
                        onClick: () => showDeleteModal(account),
                        isDestructive: true,
                    },
                ],
            ] satisfies ContextMenuItem[][]
        },
        [showEditModal, showDeleteModal]
    )

    return (
        <ConnectedAccountIntegrationSection
            type={IntegrationType.dji}
            accounts={accounts}
            onSync={showSyncModal}
            createContextMenuSections={createContextMenuSections}
        />
    )
}
